/** @jsxImportSource @emotion/react */

import { Global, css } from '@emotion/react'
import { CssBaseline } from '@mui/material'
import { Fragment, ReactElement, createContext, useEffect } from 'react';
import RequestHandler from '../../lib/request_handler'
import Article from '../Commons/Article'
import { setDecorateWebviewUrlFlag } from '../lib/useDecorateWebviewUrl'
import { FeaturedArticle, FeaturedArticleComponentProps } from '../types'
import routes from '../utils/routes'

type Props = {
  article: FeaturedArticle
  components: FeaturedArticleComponentProps
}

const getReadRatio = (): number =>
  (window.innerHeight + window.scrollY) / document.body.clientHeight

// https://github.com/kufu-ai/bargain/issues/5169 にある「同一閲覧」の同一性を担保するパラメータ
const accessId = Math.random()

type logData = {
  event: string
  contentId?: number
  contentType?: string
  shopId?: number
  scrollRate?: number
}

interface FeaturedArticleShowContextValue {
  logFeaturedArticleEvent: (logData: logData) => void
}

export const FeaturedArticleShowContext = createContext<FeaturedArticleShowContextValue>(undefined)

function PreviewComponent(props: Props): ReactElement {
  const article = props.article
  const components = props.components
  setDecorateWebviewUrlFlag()

  useEffect(() => {
    window.addEventListener('load', () => {
      setInterval(generateLogReadRatio(), 500)
    })
  })

  const generateLogReadRatio = () => {
    let maxRatio = -1.0

    type sentReadRatio = Record<number, boolean>
    const sentReadRatio: sentReadRatio = {
      0.0: false,
      0.3: false,
      0.5: false,
      0.7: false,
    }

    return () => {
      const currentRatio = getReadRatio()
      if (currentRatio <= maxRatio) {
        return
      }
      maxRatio = currentRatio

      for (const ratio of Object.keys(sentReadRatio) as unknown as Array<keyof sentReadRatio>) {
        const isSent = sentReadRatio[ratio]
        if (isSent) {
          continue
        }

        if (ratio <= currentRatio) {
          logFeaturedArticleEvent({
            event: 'scroll',
            scrollRate: Number(ratio as unknown as string),
          })
          sentReadRatio[ratio] = true
        }
      }
    }
  }

  const logFeaturedArticleEvent = ({
    event,
    shopId,
    contentType,
    contentId,
    scrollRate,
  }: logData) => {
    const data = {
      access_id: accessId,
      content_id: contentId,
      content_type: contentType,
      event: event,
      query_string: location.search,
      referer: document.referrer,
      shop_id: shopId,
      scroll_rate: scrollRate,
    }
    const handler = new RequestHandler(routes.log_featured_article_path(article.id), 'POST', data)
    handler.requestWithBody().catch((error) => {
      console.error(error)
    })
  }

  return (
    <Fragment>
      <CssBaseline />
      <Global
        styles={css`
          .article_wrapper {
            width: 100%;
          }

          @media screen and (min-width: 1050px) {
            .article_wrapper {
              max-width: 480px;
            }
          }
        `}
      />
      <FeaturedArticleShowContext.Provider
        value={{
          logFeaturedArticleEvent: logFeaturedArticleEvent,
        }}
      >
        <div css={articleWrapperStyle} className="article_wrapper">
          <Article article={article} components={components} />
        </div>
      </FeaturedArticleShowContext.Provider>
    </Fragment>
  )
}

const articleWrapperStyle = css`
  overflow-x: hidden;
  margin: 0 auto;
  padding: constant(safe-area-inset-top) constant(safe-area-inset-right)
    constant(safe-area-inset-left);
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-left);
`

export default PreviewComponent
